import { useEffect, useState } from 'react';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { workspaceApi } from './api';
import { selectedSkillSnapshot } from './skill-context';
import type { CompanySkill, WorkspaceTurnContext } from './types';
import { workspaceErrorCopy } from './workspace-errors';

export type ReferenceMetadata = {
  files: { id: string; name: string }[];
  sources: { id: string; title: string }[];
  results: { id: string; title: string }[];
};
const empty: ReferenceMetadata = { files: [], sources: [], results: [] };

// Hydrate only explicitly selected context, never the rest of a library.
// Saved Skill references and conversation attachments keep separate 30-item
// admission bounds, even when both sets are selected in the same turn.
export function useSelectedReferences(
  owner: string,
  context: WorkspaceTurnContext,
  skill?: CompanySkill,
) {
  const locale = useChatInterfaceLocale();
  const snapshot = selectedSkillSnapshot(context, skill);
  const explicit = {
    fileIds: context.fileIds,
    sourceIds: context.sourceIds,
    resultIds: context.resultIds,
  };
  const saved = {
    fileIds: snapshot?.fileIds ?? [],
    sourceIds: snapshot?.sourceIds ?? [],
    resultIds: [] as string[],
  };
  const key = JSON.stringify([owner, explicit, saved]);
  const [state, setState] = useState({ key: '', value: empty, error: '' });
  useEffect(() => {
    let active = true;
    const inputs = [explicit, saved].filter((input) =>
      Object.values(input).some((ids) => ids.length),
    );
    if (!owner || !inputs.length) {
      setState({ key, value: empty, error: '' });
      return;
    }
    void Promise.all(inputs.map((input) => workspaceApi.references(input)))
      .then((pages) => {
        if (!active) return;
        setState({
          key,
          value: {
            files: pages.flatMap((page) => page.files),
            sources: pages.flatMap((page) => page.sources),
            results: pages.flatMap((page) => page.results),
          },
          error: '',
        });
      })
      .catch(() => {
        if (active)
          setState({
            key,
            value: empty,
            error: workspaceErrorCopy(locale).referencesUnavailable,
          });
      });
    return () => {
      active = false;
    };
  }, [key, locale]);
  return state.key === key ? state : { key, value: empty, error: '' };
}
